import React, { useState, useEffect, useMemo, memo } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory } from 'react-router-dom';

import Tooltip from 'components/Tooltip';
import DashboardContainer from 'components/DashboardContainer';
import Button from 'components/Button';
import PriceInput from 'components/PriceInput';
import FormInput from 'components/FormInput';
import TextArea from 'components/TextArea';
import CheckboxWithLabel from 'components/Checkbox/WithLabel';
import UploadButton from 'components/UploadButton';
import Preview from 'components/ProductPreview';

import { showErrorMessage } from 'redux/slices/notification';
import { productNew as TEXT } from 'locales/en';
import { createProduct } from 'redux/slices/product';
import schema, { FIELD_NAMES, defaultValues as _defaultValues } from './schema';
import { BASE_URL } from 'utils/constants';
import PATHS from 'routes/paths';
import { useMediaQuery } from 'hooks';
import breakpoints from 'utils/breakpoints';

import 'utils/vapor';

const ProductNew = () => {
  const [progress, setProgress] = useState(0);
  const [imagePath, setImagePath] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const isLarge = useMediaQuery(breakpoints.lg);
  const defaultValues = history.location.state?.details || _defaultValues;

  const { register, handleSubmit, formState, control, setValue, trigger } =
    useForm({
      resolver: yupResolver(schema),
      defaultValues,
    });
  const { errors, isSubmitting } = formState;
  const [name, image, description, price, hasStockLimit, fitPhoto] = useWatch({
    control,
    name: [
      FIELD_NAMES.name,
      FIELD_NAMES.image,
      FIELD_NAMES.description,
      FIELD_NAMES.price,
      FIELD_NAMES.hasStockLimit,
      FIELD_NAMES.fitPhoto,
    ],
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      history.push(PATHS.products);
    }
  }, [history, isSubmitSuccessful]);

  const onSubmit = async (data) => {
    const { image: _image, ..._data } = data;

    try {
      if (imagePath) {
        delete _data[FIELD_NAMES.country];
        return dispatch(
          createProduct({
            ..._data,
            image: imagePath,
            image_filename: image.name,
          })
        ).then((action) => {
          if (action.type.endsWith('fulfilled')) {
            setIsSubmitSuccessful(true);
          }
        });
      }
    } catch (error) {
      return;
    }
  };

  const handleDeleteFile = () => {
    setProgress(0);
    setImageSrc(null);
    setImagePath(null);
    setValue(FIELD_NAMES.image, null);
  };

  const handleSelectFile = async (_file) => {
    const imageUrl = URL.createObjectURL(_file);
    setProgress(0);
    setImageSrc(null);
    setImagePath(null);

    setValue('image', _file, { shouldValidate: true });
    await trigger('image');

    if (errors.image?.message) return;

    const response = await Vapor.store(_file, {
      baseURL: BASE_URL,
      options: {
        withCredentials: true,
      },
      progress: (progress) => {
        setProgress(Math.round(progress * 100));
      },
    });

    if (!!response.key) {
      setImagePath(response.key);
      setImageSrc(imageUrl);
    } else {
      dispatch(showErrorMessage('Image upload failed'));

      setValue(FIELD_NAMES.image, null);
      setProgress(0);
      setImageSrc(null);
      setImagePath(null);
    }
  };

  const imagePlaceholder = useMemo(() => {
    return image?.name ?? '';
  }, [image]);

  return (
    <DashboardContainer header={TEXT.header1} subheader={TEXT.header2}>
      <div className="mt-14 flex min-h-screen-70 w-full flex-col rounded-lg bg-white p-4 py-10 shadow-md lg:flex-row lg:p-8 xl:p-12">
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          className="mr-4 w-full lg:w-7/12"
        >
          <Controller
            control={control}
            name={FIELD_NAMES.image}
            render={() => (
              <UploadButton
                label={
                  <div className="flex flex-row items-center">
                    {TEXT.image}
                    <Tooltip text={TEXT.image_tooltip} />
                  </div>
                }
                name={FIELD_NAMES.image}
                onSelectFile={handleSelectFile}
                onDeleteFile={handleDeleteFile}
                placeholder={imagePlaceholder}
                error={errors[FIELD_NAMES.image]?.message}
                progress={progress}
              />
            )}
          />
          <CheckboxWithLabel
            label={TEXT.fitPhoto}
            name={FIELD_NAMES.fitPhoto}
            register={register}
          />
          <FormInput
            label={TEXT.name}
            name={FIELD_NAMES.name}
            register={register}
            required
            error={errors[FIELD_NAMES.name]?.message}
          />
          <TextArea
            label={
              <div className="flex flex-row items-center">
                {TEXT.video}
                <Tooltip text={TEXT.video_tooltip} />
              </div>
            }
            name={FIELD_NAMES.video_embed}
            register={register}
            isDisabled
          />
          <PriceInput
            label={TEXT.price}
            name={FIELD_NAMES.price}
            control={control}
            required
            subtitle={TEXT.vatSubtitle}
            error={errors[FIELD_NAMES.price]?.message}
            thousandSeparator
            fixedDecimalScale
            decimalScale={2}
          />
          <CheckboxWithLabel
            label={TEXT.vatExclusive}
            name={FIELD_NAMES.isVatExclusive}
            register={register}
          />
          <FormInput
            label={TEXT.vatRate}
            name={FIELD_NAMES.vatRate}
            register={register}
            placeholder="21"
            inputClassName="w-20"
            type="number"
            error={errors[FIELD_NAMES.vatRate]?.message}
          />
          <TextArea
            label={TEXT.description}
            name={FIELD_NAMES.description}
            register={register}
            required
            error={errors[FIELD_NAMES.description]?.message}
          />
          <CheckboxWithLabel
            label={TEXT.availableStock}
            name={FIELD_NAMES.hasStockLimit}
            register={register}
          />
          {!!hasStockLimit && (
            <FormInput
              name={FIELD_NAMES.availableStock}
              register={register}
              subtitle={TEXT.availableStockSubtitle}
            />
          )}
          <CheckboxWithLabel
            label={TEXT.autoAccept}
            name={FIELD_NAMES.autoAccept}
            register={register}
          />
          <CheckboxWithLabel
            label={TEXT.requestInformation}
            name={FIELD_NAMES.isRequestingInformation}
            register={register}
          />
          <Button
            className="btn-primary ml-auto block h-12 w-56 px-0 lg:w-48"
            type="submit"
            title={TEXT.submit}
            showSpinner={isSubmitting}
            isDisabled={isSubmitting || (!!progress && progress < 100)}
          />
        </form>
        {isLarge && (
          <Preview
            image={imageSrc}
            name={name}
            description={description}
            price={price}
            fitPhoto={fitPhoto}
          />
        )}
      </div>
    </DashboardContainer>
  );
};

export default memo(ProductNew);
